import { Alert } from '@foam/buoy/src/components/Alert/Alert';
import { Button } from '@foam/buoy/src/components/Button/Button';
import { toast } from '@foam/buoy/src/components/Toast/Toast';
import { Form, Formik, FormikProps } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LeavePageBoundary from '~/components/LeavePageBoundary/LeavePageBoundary';
import { fetchCreator } from '~/ducks/creator';
import { useUpdateCreatorProfile } from '~/hooks/api/useUpdateCreatorProfile';
import { selectCreator } from '~/selectors/creator';
import { formatDateForPicker, validateEmail } from '~/utils-ts';

import { Email } from './Email';
import { Links } from './Links';
import { ProfileBio } from './ProfileBio';
import { ProfileLocationGenderDob } from './ProfileLocationGenderDob';
import { ProfileNameAndAvatar } from './ProfileNameAndAvatar';
import { ProfileVerticals } from './ProfileVerticals';
import { ProfileFormValues } from './types';
import { getChangedFields } from './useProfileFieldComparison';

interface CreatorProfileSettingsFormProps {
  showTitle?: boolean;
  showActionButtons?: boolean;
  showLeavePageBoundary?: boolean;
  renderCustomActions?: (props: {
    dirty: boolean;
    isLoading: boolean;
    resetForm: () => void;
    submitForm: () => void;
  }) => React.ReactNode;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const CreatorProfileSettingsForm: React.FC<
  CreatorProfileSettingsFormProps
> = ({
  showTitle = true,
  showActionButtons = true,
  showLeavePageBoundary = true,
  renderCustomActions,
  onSuccess,
  onCancel,
}) => {
  const creator = useSelector(selectCreator);
  const dispatch = useDispatch();
  const [showError, setShowError] = useState(false);

  const updateCreatorMutation = useUpdateCreatorProfile();

  const initialValues: ProfileFormValues = useMemo(() => {
    return {
      firstName: creator?.firstName || creator?.fullName?.split(' ')[0] || '',
      lastName: creator?.lastName || '',
      avatar: creator?.avatar || null,
      bio: creator?.bioAgency || creator?.biography || '',
      location: creator?.location || null,
      dateOfBirth: formatDateForPicker(creator?.dateOfBirth),
      gender: creator?.genderAgency || null,
      verticals: creator?.categoryAgency
        ? creator.categoryAgency.split(',')
        : [],
      webLinks: creator?.webLinks || [],
      email: creator?.emailContact || '',
      id: creator?.id,
      updatedAt: creator?.updatedAt,
    };
  }, [creator]);

  const handleSubmit = useCallback(
    (values: ProfileFormValues) => {
      if (!creator?.id) {
        setShowError(true);
        return;
      }
      setShowError(false); // Reset error

      // Get only the changed fields
      const changedFields = getChangedFields(initialValues, values);
      changedFields.emailContact = values.email; // needed for setting BE flag

      // If no fields have changed, don't send anything to the backend
      if (Object.keys(changedFields).length === 0) {
        return;
      }

      updateCreatorMutation.mutate(changedFields, {
        onError: () => {
          setShowError(true);
        },
        onSuccess: async () => {
          setShowError(false);

          // Refetch creator
          dispatch(fetchCreator({ id: creator?.id }));

          toast('Profile successfully updated', {
            variant: 'success',
            duration: 3000,
          });

          onSuccess?.();
        },
      });
    },
    [creator, initialValues, onSuccess, dispatch, updateCreatorMutation],
  );

  const validate = useCallback((values: ProfileFormValues) => {
    const errors: any = {};

    if (!values.firstName || values.firstName.trim() === '') {
      errors.firstName = 'First name is required';
    }

    if (!validateEmail(values.email as string)) {
      errors.email = 'Enter valid email';
    }
    if (!values.email || values.email.trim() === '') {
      errors.email = 'Contact email is required';
    }
    if (
      values.location &&
      typeof values.location === 'object' &&
      values.location.invalid
    ) {
      errors.location = 'Invalid location';
    }

    return errors;
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
      validate={validate}
    >
      {({ dirty, resetForm, submitForm }: FormikProps<ProfileFormValues>) => {
        const handleCancel = () => {
          resetForm();
          onCancel?.();
        };

        return (
          <Form>
            <div className="flex flex-col gap-6">
              {showTitle && (
                <h1 className="text-24 font-500">Profile Settings</h1>
              )}

              {showError && (
                <Alert
                  variant="error"
                  title="We've encountered an error."
                  subtitle="Please reload and try again. If the issue persists, contact us."
                  onClose={() => setShowError(false)}
                />
              )}

              <ProfileNameAndAvatar />
              <Email />
              <ProfileLocationGenderDob />
              <ProfileBio />
              <Links name="webLinks" />
              <ProfileVerticals />

              {/* Custom actions or default action buttons */}
              {renderCustomActions ? (
                renderCustomActions({
                  dirty,
                  isLoading: updateCreatorMutation.isLoading,
                  resetForm: handleCancel,
                  submitForm,
                })
              ) : showActionButtons ? (
                <div className="sticky bottom-0 z-10 flex justify-end gap-4 bg-neutral-0 py-6">
                  <Button
                    type="button"
                    variant="tertiary"
                    disabled={updateCreatorMutation.isLoading || !dirty}
                    onClick={handleCancel}
                    track-id="foam-profile-settings-cancel"
                    data-testid="foam-profile-settings-cancel"
                    size="lg"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={updateCreatorMutation.isLoading || !dirty}
                    loading={updateCreatorMutation.isLoading}
                    track-id="foam-profile-settings-save"
                    data-testid="foam-profile-settings-save"
                    size="lg"
                  >
                    Save
                  </Button>
                </div>
              ) : null}

              {showLeavePageBoundary && <LeavePageBoundary isEnabled={dirty} />}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
